import { useMemo } from 'react';
import { SystemEventTypeEnum, TPlayerActionData, TPlayerActionHandlers, TPlayerActionOption, TWAction } from '@libs/types';
import { applyLocalData, eventBus } from '@libs/core';
import { ConsumerActionPlayerNameEnums } from '../types/enums';
import { redirectTo } from '../utils/redirect';
import { sanitizeHttpUrl } from '../utils/sanitize';

declare const APP_TITLE: string;

export const usePlayerActionHandlers = (): TPlayerActionHandlers => {
    return useMemo<TPlayerActionHandlers>(() => {
        const handler = (action: TWAction, _options?: TPlayerActionOption, value?: TPlayerActionData, finalValue?: TPlayerActionData) => {
            switch (action.name) {
                case ConsumerActionPlayerNameEnums.share: {
                    const text = value as string;
                    if (navigator.share) {
                        navigator.share({ title: APP_TITLE, text }).catch(() => window.open(text, '_blank'));
                    } else {
                        window.open(text, '_blank');
                    }
                    break;
                }
                case ConsumerActionPlayerNameEnums.execute:
                    if (action.widgetId) {
                        eventBus.emit(SystemEventTypeEnum.EXECUTE, action.widgetId, value as string);
                    }
                    break;
                case ConsumerActionPlayerNameEnums.changeLanguage:
                    // Alias kept for back-compat: delegate to the generic `save_local_data:lang`
                    // mechanism, which persists the value and applies the `setLocale` side effect.
                    applyLocalData('lang', finalValue);
                    break;
                case ConsumerActionPlayerNameEnums.redirect: {
                    const url = sanitizeHttpUrl(value as string);
                    if (url) {
                        redirectTo(url);
                    }
                    break;
                }
                case ConsumerActionPlayerNameEnums.lock_source: {
                    if (action.widgetId) {
                        eventBus.emit(ConsumerActionPlayerNameEnums.lock_source, action.widgetId, finalValue as string);
                    }
                    break;
                }
            }
        };

        const names = [
            ConsumerActionPlayerNameEnums.share,
            ConsumerActionPlayerNameEnums.execute,
            ConsumerActionPlayerNameEnums.changeLanguage,
            ConsumerActionPlayerNameEnums.redirect,
            ConsumerActionPlayerNameEnums.lock_source,
        ];
        return Object.fromEntries(names.map((name) => [name, handler])) as TPlayerActionHandlers;
    }, []);
};
